import {
  Badge,
  Button,
  CheckShieldIcon,
  CloseIconThin,
} from "@cmsgov/ds-medicare-gov";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { FunctionComponent, useContext } from "react";
import { FormattedMessage } from "react-intl-phraseapp";
import { YearOverYearExploreOptionsButton } from ".";
import { DrugCosts, Plan } from "../../../@types";
import routes from "../../../app/routes";
import { AppStore } from "../../../app/store";
import { EnrollButtonForm, SuppressionHandler } from "../../../components";
import {
  useTranslate,
  isCurrentPlan,
  $$,
  isMapdPlanType,
  isMaPlanType,
  useCosts,
  makePlanLongId,
} from "../../../helpers";
import englishTranslations from "../../../translations/en-US.json";
import { getPlanName } from "../../../helpers/planHelpers";
import { getPlanNameElementId } from "./planHeaderHelpers";
import { useHistory } from "react-router";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4ParentComponentType,
  PlanCompareAnalyticsSubsection,
} from "../../../app/contexts/Analytics/types";

export const planHeaderPlanDetailsButtonId = "plan-header-plan-details-button";

const PlanHeader: FunctionComponent<{
  noLeftBorder?: boolean;
  onPlanRemove?: (plan: Plan) => void;
  plan: Plan;
  planNameId?: string;
  isYearOverYear: boolean;
}> = ({
  noLeftBorder,
  onPlanRemove,
  plan,
  planNameId = getPlanNameElementId(plan),
  isYearOverYear = false,
}) => {
  const t = useTranslate();
  const { state, dispatch } = useContext(AppStore.AppContext);
  const { currentCoverage, nextYearCoverage, language, pharmacies } = state;
  const flags = useFlags();
  const history = useHistory();
  const aria_string = `${t("plan_card.enroll_in")} ${getPlanName(
    plan,
    language
  )}`;

  const currentPlan = isYearOverYear
    ? isCurrentPlan(currentCoverage, plan)
    : isCurrentPlan(currentCoverage, plan) ||
      isCurrentPlan(nextYearCoverage, plan);

  const showPlanDetailsButton = !(
    isYearOverYear && plan.contract_year === flags.mctCurrentPlanYear.toString()
  );

  const planName = getPlanName(plan, language);

  const { costs } = useCosts(plan, undefined, pharmacies, isYearOverYear);

  const handleGoToPlan = (plan: Plan) => {
    history.push(`${routes.planDetails.replace(":id", makePlanLongId(plan))}`);
  };

  return (
    <div
      className={`PlanComparePage__planHeader${
        noLeftBorder ? " ds-u-border-left--0" : ""
      }`}
    >
      <div className="ds-u-lg-padding-x--3">
        <div>
          {isYearOverYear && (
            <Badge className="YearOverYear__planBadge">
              <CheckShieldIcon className="ds-u-margin-right--1 ds-c-icon-color--primary" />{" "}
              <span className="ds-u-font-size--base">
                <FormattedMessage
                  id="plan_compare.plan_header.your_plan"
                  values={{ contractYear: plan.contract_year }}
                />
              </span>
            </Badge>
          )}

          <h3 className="ds-u-margin-bottom--1 ds-u-margin-top--3 ds-u-color--primary ds-u-display--flex ds-u-justify-content--between">
            <span
              id={planNameId}
              className="PlanComparePage__planHeaderPlanName e2e-pc-plan-name"
            >
              {planName}
            </span>
            {onPlanRemove && !isYearOverYear && (
              <Button
                variation="ghost"
                size="small"
                className="PlanComparePage__close mct-hide-print"
                aria-label={`Remove ${planName} plan`}
                onClick={() => {
                  dispatch({
                    type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                    settings: {
                      button: {
                        buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                        buttonType: AnalyticsButtonType.BUTTON,
                        text: englishTranslations["close"],
                      },
                    },
                  });
                  onPlanRemove(plan);
                }}
              >
                <CloseIconThin />
              </Button>
            )}
          </h3>
        </div>
        <PlanPremium
          plan={plan}
          isYearOverYear={isYearOverYear}
          costs={costs}
        />
        <div className="ds-u-margin-bottom--3 mct-hide-print">
          <div className="ds-l-row">
            {!currentPlan && !isYearOverYear && (
              <EnrollButtonForm
                plan={plan}
                aria_string={aria_string}
                tealiumEventAction="Find Plans - Compare"
                buttonClassName="ds-c-button ds-c-button--solid ds-c-button--alternate mct-u-width--full"
                dialogClassName="PlanComparePage__enrollDialog ds-l-col--12 ds-l-sm-col--6 ds-l-lg-col--auto"
              />
            )}

            {isYearOverYear && !currentPlan && (
              <div className="ds-l-col--12 ds-l-sm-col--6 ds-l-lg-col--auto">
                <YearOverYearExploreOptionsButton
                  subsection={PlanCompareAnalyticsSubsection.NEXT_PLAN_YEAR}
                  className="mct-u-width--full"
                />
              </div>
            )}

            {showPlanDetailsButton && (
              <div className="ds-l-col--12 ds-l-sm-col--6 ds-l-lg-col--auto">
                <Button
                  onClick={(): void => {
                    dispatch({
                      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                      settings: {
                        event_action: "Find Plans - Compare",
                        event_label: `Plan Click: ${planName}`,
                      },
                    });
                    dispatch({
                      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                      settings: {
                        button: {
                          buttonStyle: AnalyticsButtonStyle.DEFAULT,
                          buttonType: AnalyticsButtonType.BUTTON,
                          text: englishTranslations["plan_card.plan_details"],
                        },
                        linkUrl: `${routes.planDetails.replace(
                          ":id",
                          makePlanLongId(plan)
                        )}`,
                        custom: {
                          parent_component_heading:
                            isYearOverYear && !currentPlan
                              ? "next year plan"
                              : "current year plan",
                          parent_component_type:
                            Ga4ParentComponentType.PLAN_HEADER,
                        },
                      },
                    });
                    handleGoToPlan(plan);
                  }}
                  className="PlanComparePage__planDetailsBtn e2e-pc-plan-details-button mct-u-width--full"
                  aria-label={"Plan details for " + planName}
                  data-testid={planHeaderPlanDetailsButtonId}
                >
                  {t("plan_card.plan_details")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isYearOverYear && currentPlan && (
        <div className="PlanComparePage__currentPlanBanner">
          {t("plan_card.your_current_plan")}
        </div>
      )}
    </div>
  );
};

const PlanPremium: FunctionComponent<{
  plan: Plan;
  isYearOverYear: boolean;
  costs: DrugCosts[];
}> = ({ plan, isYearOverYear = false, costs }) => {
  const t = useTranslate();
  let label;

  if (isMapdPlanType(plan.plan_type)) {
    label = t("plan_terms.monthly_plan_premium_mapd");
  } else if (isMaPlanType(plan.plan_type)) {
    label = t("plan_card.monthly_premium_ma");
  } else {
    label = t("plan_terms.monthly_plan_premium_pdp");
  }

  const planYearlyPremium = plan.calculated_monthly_premium * 12;
  const estimatedDrugAndPremiumCosts = costs.map(
    pharmacy => pharmacy.estimated_yearly_total + planYearlyPremium
  );

  const minEstimatedDrugAndPremiumCost = estimatedDrugAndPremiumCosts.length
    ? Math.min(...estimatedDrugAndPremiumCosts)
    : 0;

  return (
    <div className="PlanComparePage__planPremium">
      {!isYearOverYear && (
        <>
          <p className="ds-text-heading--lg ds-u-margin-bottom--1">
            <SuppressionHandler
              checkShouldSuppressDrugPricing={true}
              redactions={plan.redactions}
            >
              {$$(plan.calculated_monthly_premium)}
            </SuppressionHandler>
          </p>{" "}
          <p className="ds-u-margin--0">{label}</p>
        </>
      )}

      {isYearOverYear && (
        <>
          <p className="ds-text-heading--lg ds-u-margin-bottom--1">
            <SuppressionHandler
              checkShouldSuppressDrugPricing={true}
              redactions={plan.redactions}
            >
              {$$(minEstimatedDrugAndPremiumCost)}
            </SuppressionHandler>
          </p>{" "}
          <p className="ds-u-margin--0">
            {t("plan_terms.estimated_yearly_total_cost")}
          </p>
        </>
      )}
    </div>
  );
};

export default PlanHeader;
